import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import {
  CaptureUpdateAction,
  convertToExcalidrawElements,
} from "@excalidraw/excalidraw";
import {
  TTDStreamFetch,
  type TTDStreamFetchResult,
  type ClarificationInfo,
} from "@excalidraw/excalidraw/components/TTDDialog/utils/TTDStreamFetch";
import { parseMermaidToExcalidraw } from "@excalidraw/mermaid-to-excalidraw";

import { randomId } from "@excalidraw/common";
import { syncInvalidIndices } from "@excalidraw/element";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import { useSetAtom } from "../app-jotai";
import { ensureXiaolaiFont } from "../utils/ensureXiaolaiFont";
import { reorderParentBeforeChild } from "../utils/elementReorder";
import { useAppI18n } from "../hooks/useAppI18n";
import { diagramJustGeneratedAtom } from "../hooks/useAIVideoSignal";
import { TextToExcalidrawStreamFetch } from "../utils/TextToExcalidrawStreamFetch";
import { generateProjectNameViaLLM } from "../utils/generateProjectName";
import { getBackendUrl } from "../utils/backendUrl";
import { useTestTelemetry } from "../hooks/useTestTelemetry";

import { errorCollector } from "../utils/errorCollector";

import { getOrCreateAnonymousId } from "../utils/anonymousId";

import { getPendingChatHistoryForDiagram } from "../utils/conversationStorage";

import { VoiceInputButton } from "./VoiceInputButton";
import { AttachImageThumbnails, readFilesAsDataUrls } from "./AttachImage";
import { AttachFileThumbnails, readFilesAsBase64, type AttachedFile } from "./AttachFile";
import { AttachButton } from "./AttachButton";
import { MessageAttachments } from "./MessageAttachments";
import { UploadToast } from "./UploadToast";
import { useFileSizeToast } from "../hooks/useFileSizeToast";

import "./AIWeaverSidebar.scss";

import type { ChatMessage } from "../data/diagramStorage";

// Global tracker to prevent duplicate initial prompt processing across StrictMode remounts
const processedInitialPrompts = new Set<string>();

// Helper function to get user-friendly phase display names
/** Detect if user message expresses regenerate intent (new generation with same conditions). */
function isRegenerateIntent(text: string): boolean {
  const t = text.trim().toLowerCase();
  const patterns = [
    /^\s*regenerate\s*$/i,
    /^\s*redraw\s*$/i,
    /^\s*recreate\s*$/i,
    /^\s*start\s+over\s*$/i,
    /^\s*from\s+scratch\s*$/i,
    /^\s*重新生成\s*$/,
    /^\s*重新画\s*$/,
    /^\s*重新绘制\s*$/,
    /^\s*从头开始\s*$/,
  ];
  return patterns.some((p) => p.test(t));
}

interface MessageAttachmentMeta {
  filename: string;
  mimeType: string;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  imageUrls?: string[];
  attachmentFiles?: MessageAttachmentMeta[];
}

interface AIWeaverSidebarProps {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  initialPrompt?: string;
  initialPlan?: string;
  /** From Go to Draw: send in first request; backend runs Summary then Generation. Cleared after first use. */
  initialConversationContext?: import("./ConversationContent").ConversationContextForDraw;
  onConversationContextUsed?: () => void;
  /** Images attached from EntryScreen Draw Direct. Used once in first generation, then cleared. */
  initialDrawImageUrls?: string[];
  /** Files attached from EntryScreen Draw Direct. Used once in first generation, then cleared. */
  initialDrawFiles?: import("./AttachFile").AttachedFile[];
  onDrawAttachmentsUsed?: () => void;
  isExcalidrawReady?: boolean;
  // Authentication status - when false, diagram generation works but won't persist to cloud
  isAuthenticated?: boolean;
  // User ID for quota (24h rolling window). When set, backend enforces generation limit.
  userId?: string | null;
  // Chat history persistence
  currentDiagramId?: string | null;
  currentDiagramName?: string;
  savedChatHistory?: ChatMessage[];
  onChatHistoryChange?: (messages: ChatMessage[]) => void;
  // Auto-create diagram when sending first message with no project (only when authenticated)
  onCreateNewDiagram?: (
    name?: string,
    elements?: readonly ExcalidrawElement[],
    chatHistory?: ChatMessage[],
  ) => Promise<{ id: string } | null>;
  onRenameDiagram?: (name: string) => Promise<void>;
  onRenameDiagramById?: (id: string, name: string) => Promise<void>;
  // Open the auth/sign-up modal (for quota limit upsell)
  onOpenAuthModal?: () => void;
  // "Back to Conversation" from canvas sidebar
  hasPendingConversation?: boolean;
  onBackToConversation?: () => void;
}

interface Operation {
  type: "modify" | "add" | "delete";
  elementId?: string;
  changes?: Record<string, unknown>;
  element?: Record<string, unknown>;
}

interface CanvasEditResponse {
  operations: Operation[];
  error?: string;
  action?: "edit" | "regenerate";
  regenerate_prompt?: string;
}

export const AIWeaverSidebar: React.FC<AIWeaverSidebarProps> = ({
  excalidrawAPI,
  isCollapsed,
  onToggleCollapse,
  initialPrompt,
  initialPlan,
  initialConversationContext,
  onConversationContextUsed,
  initialDrawImageUrls,
  initialDrawFiles,
  onDrawAttachmentsUsed,
  isExcalidrawReady = false,
  isAuthenticated = false,
  userId = null,
  currentDiagramId,
  currentDiagramName,
  savedChatHistory,
  onChatHistoryChange,
  onCreateNewDiagram,
  onRenameDiagram,
  onRenameDiagramById,
  onOpenAuthModal,
  hasPendingConversation: _hasPendingConversation = false,
  onBackToConversation: _onBackToConversation,
}) => {
  const { t: appT } = useAppI18n();
  const setDiagramJustGenerated = useSetAtom(diagramJustGeneratedAtom);
  const { toastMessage, showFileTooLarge, dismissToast } = useFileSizeToast();

  useEffect(() => {
    ensureXiaolaiFont();
  }, []);

  // Convert ChatMessage to Message (add Date object from ISO string)
  const convertToMessage = useCallback(
    (chatMsg: ChatMessage): Message => ({
      id: chatMsg.id,
      role: chatMsg.role,
      content: chatMsg.content,
      timestamp: new Date(chatMsg.timestamp),
      attachmentFiles: chatMsg.attachmentFiles,
    }),
    [],
  );

  // Convert Message to ChatMessage (convert Date to ISO string)
  // Only stores lightweight metadata — no image data URLs (too large for DB)
  const convertToChatMessage = useCallback(
    (msg: Message): ChatMessage => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp.toISOString(),
      imageCount: msg.imageUrls?.length,
      attachmentFiles: msg.attachmentFiles,
    }),
    [],
  );

  const [messages, setMessages] = useState<Message[]>([]);
  const [_hasInitializedFromSaved, setHasInitializedFromSaved] =
    useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [changeSummary, setChangeSummary] = useState<string | null>(null);
  // Chart type is auto-detected by the AI agent
  const selectedChartType = "auto";
  // Always use direct Excalidraw generation (new pipeline)
  const useDirectExcalidraw = true;
  const [workflowPhase, setWorkflowPhase] = useState<string | null>(null);
  const [quota, setQuota] = useState<{
    used: number;
    limit: number;
    remaining: number;
    windowHours: number;
    isAnonymous?: boolean;
  } | null>(null);
  const anonymousId = useMemo(getOrCreateAnonymousId, []);
  /** Last action (generation or edit) – prompt + type; used for Regenerate. */
  const [lastAction, setLastAction] = useState<{
    prompt: string;
    type: "generation" | "edit";
  } | null>(null);
  /** Canvas snapshot taken right before the last action, so Regenerate can restore it. */
  const snapshotBeforeLastActionRef = useRef<readonly any[] | null>(null);
  /** Show the quota-limit modal when user tries to generate after exhausting quota. */
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);

  const handleUpgradeToPro = useCallback(async () => {
    if (!userId) {
      return;
    }
    const base = getBackendUrl();
    setIsUpgradeLoading(true);
    try {
      const res = await fetch(`${base}/v1/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          price_id: "price_1T7PvlKBCcpgOgYlpWbcBBCe",
          user_id: userId,
          success_url: window.location.origin,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data.detail || data.message || `Request failed (${res.status})`;
        throw new Error(msg);
      }
      const checkout_url = data.checkout_url;
      if (!checkout_url) {
        throw new Error("No checkout URL returned");
      }
      window.location.href = checkout_url;
    } catch (e) {
      setIsUpgradeLoading(false);
      const message = e instanceof Error ? e.message : "Checkout failed";
      alert(message);
    }
  }, [userId]);
  const { collectAndSendReport } = useTestTelemetry();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const skipNextEnterRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  /** When coming from Conversation "Go to Draw", we send plan in the first request. Used only once. */
  const initialPlanUsedRef = useRef(false);
  /** When coming from Go to Draw with conversation context, we send conversationContext in the first request. Used only once. */
  const initialConversationContextUsedRef = useRef(false);
  /** Draw attachments (images + files) from EntryScreen: used once in first generation. */
  const initialDrawAttachmentsUsedRef = useRef(false);
  /** When backend sends the summary (Go to Draw flow), we store it for display and for Regenerate. Cleared after setting lastAction. */
  const summaryForDisplayAndRegenerateRef = useRef<string | null>(null);

  // Pending attachments in sidebar input
  const [pendingImageUrls, setPendingImageUrls] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  // Ref to pass current-send attachments into generation functions without changing signatures
  const sendAttachmentsRef = useRef<{
    imageUrls: string[];
    files: AttachedFile[];
  }>({ imageUrls: [], files: [] });

  // Track previous diagram ID to detect diagram changes
  const prevDiagramIdRef = useRef<string | null | undefined>(currentDiagramId);

  // Track if we're auto-creating a diagram (to preserve messages during the switch)
  const isAutoCreatingDiagramRef = useRef(false);

  // Track which diagrams we've already auto-renamed to avoid repeated renames
  const autoRenamedDiagramIdsRef = useRef<Set<string>>(new Set());
  // Last known diagram id for renaming (props may lag behind after create)
  const lastKnownDiagramIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (currentDiagramId) {
      lastKnownDiagramIdRef.current = currentDiagramId;
    }
  }, [currentDiagramId]);

  // When leaving Canvas (unmount), abort any in-flight generation so we don't keep running in the background
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Quota: logged-in 10/24h, anonymous 2/24h
  const quotaKey = userId ?? (anonymousId || null);
  const fetchQuota = useCallback(async () => {
    if (!quotaKey) {
      setQuota(null);
      return;
    }
    const base = getBackendUrl();
    if (!base) {
      return;
    }
    const param = userId
      ? `userId=${encodeURIComponent(userId)}`
      : `anonymousId=${encodeURIComponent(anonymousId)}`;
    try {
      const res = await fetch(`${base}/v1/ai/quota?${param}`);
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      setQuota({
        used: data.used ?? 0,
        limit: data.limit ?? 10,
        remaining: data.remaining ?? 0,
        windowHours: data.windowHours ?? 24,
        isAnonymous: data.isAnonymous ?? false,
      });
    } catch {
      setQuota(null);
    }
  }, [userId, anonymousId, quotaKey]);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading && quotaKey) {
      fetchQuota();
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, quotaKey, fetchQuota]);

  const generateDiagramTitle = useCallback((prompt: string) => {
    const cleaned = prompt
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // Take the first clause/sentence for a compact title
    const firstChunk = cleaned
      .split(/[\n。！？!?；;，,]/)
      .map((s) => s.trim())
      .find(Boolean);

    let title = firstChunk || cleaned;

    // Remove some common leading phrases (CN/EN) to make titles cleaner
    title = title
      .replace(
        /^(请|帮我|帮忙|麻烦|我要|我想|我希望|生成|创建|画(一个|一张)?|做(一个|一张)?|设计|create|generate|make)\s*/i,
        "",
      )
      .replace(/^a\s+/i, "")
      .trim();

    // Hard cap length (aim for a short, readable title)
    const MAX_LEN = 24;
    if (title.length > MAX_LEN) {
      title = title.slice(0, MAX_LEN).trim();
    }

    if (!title) {
      return "New Diagram";
    }

    return title;
  }, []);

  const maybeAutoRenameDiagram = useCallback(
    async (firstPrompt: string) => {
      if (!isAuthenticated) {
        return;
      }
      if (!onRenameDiagram && !onRenameDiagramById) {
        return;
      }

      const diagramId =
        lastKnownDiagramIdRef.current || currentDiagramId || null;

      if (diagramId && autoRenamedDiagramIdsRef.current.has(diagramId)) {
        return;
      }

      const nameBelongsToThisDiagram =
        currentDiagramId != null &&
        diagramId != null &&
        currentDiagramId === diagramId;

      const normalizedName = currentDiagramName?.trim().toLowerCase() || "";
      const isUntitledDiagram =
        !normalizedName ||
        normalizedName === "untitled diagram" ||
        normalizedName === "untitled" ||
        normalizedName.startsWith("untitled ");

      if (
        nameBelongsToThisDiagram &&
        currentDiagramName &&
        !isUntitledDiagram
      ) {
        if (diagramId) {
          autoRenamedDiagramIdsRef.current.add(diagramId);
        }
        return;
      }

      let title: string | null = null;
      const promptForNaming =
        firstPrompt.length > 500
          ? `${firstPrompt.substring(0, 500)}...`
          : firstPrompt;

      try {
        title = await generateProjectNameViaLLM(promptForNaming);
      } catch {
        // Fall through to client fallback
      }

      if (!title) {
        title = generateDiagramTitle(firstPrompt);
      }

      if (!title || title === "Untitled Diagram") {
        return;
      }

      if (!diagramId) {
        return;
      }

      try {
        if (onRenameDiagramById) {
          await onRenameDiagramById(diagramId, title);
        } else if (onRenameDiagram) {
          await onRenameDiagram(title);
        } else {
          return;
        }
        autoRenamedDiagramIdsRef.current.add(diagramId);
      } catch (e) {
        console.warn("[Naming] rename failed", { diagramId, title, error: e });
      }
    },
    [
      isAuthenticated,
      currentDiagramId,
      currentDiagramName,
      onRenameDiagram,
      onRenameDiagramById,
      generateDiagramTitle,
    ],
  );

  // Reset messages when diagram changes
  useEffect(() => {
    const prevId = prevDiagramIdRef.current;

    // Diagram changed (including from undefined to a new ID, or from one ID to another)
    if (prevId !== currentDiagramId) {
      if (import.meta.env.DEV) {
        console.log(
          "[AIWeaverSidebar] Diagram changed from",
          prevId,
          "to",
          currentDiagramId,
        );
        console.log(
          "[AIWeaverSidebar] isAutoCreatingDiagram:",
          isAutoCreatingDiagramRef.current,
        );
      }

      // If we're auto-creating a diagram, don't clear messages - they were just added
      if (isAutoCreatingDiagramRef.current) {
        if (import.meta.env.DEV) {
          console.log(
            "[AIWeaverSidebar] Skipping message reset - auto-creating diagram",
          );
        }
        isAutoCreatingDiagramRef.current = false;
        prevDiagramIdRef.current = currentDiagramId;
        setHasInitializedFromSaved(true);
        return;
      }

      // Load saved chat history for the new diagram, or clear if empty
      if (savedChatHistory && savedChatHistory.length > 0) {
        const loadedMessages = savedChatHistory.map(convertToMessage);
        setMessages(loadedMessages);
        if (import.meta.env.DEV) {
          console.log(
            "[AIWeaverSidebar] Loaded",
            loadedMessages.length,
            "messages from cloud",
          );
        }
      } else {
        // New or empty diagram - clear messages
        if (import.meta.env.DEV) {
          console.log(
            "[AIWeaverSidebar] Clearing messages for new/empty diagram",
          );
        }
        setMessages([]);
      }
      setLastAction(null);
      snapshotBeforeLastActionRef.current = null;
      setHasInitializedFromSaved(true);
      prevDiagramIdRef.current = currentDiagramId;
    }
  }, [currentDiagramId, savedChatHistory, convertToMessage]);

  // Save chat history when messages change
  useEffect(() => {
    // Only save if we have messages and the callback is provided
    if (onChatHistoryChange && messages.length > 0) {
      const chatHistory = messages.map(convertToChatMessage);
      onChatHistoryChange(chatHistory);
      if (import.meta.env.DEV) {
        console.log(
          "[AIWeaverSidebar] Saving",
          chatHistory.length,
          "messages to cloud",
        );
      }
    }
  }, [messages, onChatHistoryChange, convertToChatMessage]);

  // Handle initial prompt from entry screen
  // Wait for excalidrawAPI AND Excalidraw to be fully ready before sending
  // Uses global Set to prevent duplicate sends across StrictMode remounts
  useEffect(() => {
    // Check if we have an initial prompt that hasn't been processed yet
    // IMPORTANT: Wait for isExcalidrawReady to ensure scene initialization is complete
    if (
      initialPrompt &&
      excalidrawAPI &&
      isExcalidrawReady &&
      !processedInitialPrompts.has(initialPrompt)
    ) {
      // Mark as processed immediately to prevent duplicate calls
      processedInitialPrompts.add(initialPrompt);
      handleSendMessage(initialPrompt);

      // Clear the processed prompt after a delay to allow same prompt in future sessions
      // This handles the case where user goes back to entry and submits the same prompt again
      setTimeout(() => {
        processedInitialPrompts.delete(initialPrompt);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, excalidrawAPI, isExcalidrawReady]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea based on content (ChatGPT-style expanding)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto so scrollHeight recalculates correctly
      textarea.style.height = "auto";
      // Cap at ~4 lines: line-height 1.5 * 16px = 24px/line, 4 lines = 96px + 20px padding
      const maxHeight = 116;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  }, [inputValue]);

  // Hide change summary after 5 seconds
  useEffect(() => {
    if (changeSummary) {
      const timer = setTimeout(() => setChangeSummary(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [changeSummary]);

  /** Clear canvas elements so a new generation replaces previous content. */
  const _clearCanvas = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }
    excalidrawAPI.updateScene({
      elements: [],
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }, [excalidrawAPI]);

  const executeOperations = useCallback(
    (operations: Operation[]) => {
      if (!excalidrawAPI) {
        return false;
      }

      const currentElements = excalidrawAPI.getSceneElements();
      const newElements: any[] = [...currentElements];
      let hasChanges = false;
      let addedCount = 0;
      let modifiedCount = 0;
      let deletedCount = 0;

      for (const op of operations) {
        if (op.type === "modify" && op.elementId && op.changes) {
          const index = newElements.findIndex((el) => el.id === op.elementId);
          if (index !== -1) {
            const element = newElements[index];

            // FIX: Sync originalText when text is modified for text elements
            // This ensures double-click edit mode shows the updated text
            const changes = { ...op.changes };
            const hasTextChange = "text" in changes;
            const isTextElement = element.type === "text";
            const isBoundText = !!(element as any).containerId;
            if (hasTextChange && isTextElement) {
              // Sync originalText with text to prevent revert on double-click
              (changes as any).originalText = changes.text;
              // Enable autoResize so width adjusts to new text
              (changes as any).autoResize = true;

              // For bound text (text inside containers), we need to unbind it
              // Otherwise Excalidraw will re-wrap the text based on container width
              if (isBoundText) {
                const containerId = (element as any).containerId;
                // Unbind the text from container
                (changes as any).containerId = null;

                // Calculate new width based on text length and font size
                const newText = changes.text as string;
                const fontSize = (element as any).fontSize || 20;
                const estimatedWidth = Math.max(
                  newText.length * fontSize * 0.6,
                  100,
                );
                (changes as any).width = estimatedWidth;

                // Also update the container to remove the bound text reference
                const containerIndex = newElements.findIndex(
                  (el) => el.id === containerId,
                );
                if (containerIndex !== -1) {
                  const container = newElements[containerIndex];
                  const boundElements = (container.boundElements || []).filter(
                    (be: any) => be.id !== element.id,
                  );
                  newElements[containerIndex] = {
                    ...container,
                    boundElements:
                      boundElements.length > 0 ? boundElements : null,
                    version: (container.version ?? 0) + 1,
                    versionNonce: Math.floor(Math.random() * 1000000000),
                    updated: Date.now(),
                  };
                }
              }
            }

            const merged = {
              ...element,
              ...changes,
              version: (element.version ?? 0) + 1,
              versionNonce: Math.floor(Math.random() * 1000000000),
              updated: Date.now(),
            };
            // Ensure linear elements never have undefined/empty points (prevents isPathALoop crash)
            const el = merged as any;
            if (
              (el.type === "arrow" || el.type === "line") &&
              (!el.points || !Array.isArray(el.points) || el.points.length < 2)
            ) {
              const fallback =
                (element as any).points &&
                Array.isArray((element as any).points) &&
                (element as any).points.length >= 2
                  ? (element as any).points
                  : [
                      [0, 0],
                      [el.width ?? 100, el.height ?? 0],
                    ];
              el.points = fallback;
            }
            newElements[index] = merged;
            hasChanges = true;
            modifiedCount++;
          }
        } else if (op.type === "add" && op.element) {
          // Add new element - ensure required fields have defaults
          const raw = op.element as any;
          const textOrOriginal = raw.originalText ?? raw.text ?? "";
          const w = raw.width ?? 100;
          const h = raw.height ?? 100;
          // Linear elements (arrow/line) must have valid points to avoid isPathALoop crash
          const pointsForLinear =
            raw.type === "arrow" || raw.type === "line"
              ? Array.isArray(raw.points) && raw.points.length >= 2
                ? raw.points
                : [
                    [0, 0],
                    [w, h],
                  ]
              : undefined;
          const newElement = {
            ...raw,
            id: (raw.id as string) || randomId(),
            version: 1,
            versionNonce: Math.floor(Math.random() * 1000000000),
            isDeleted: false,
            updated: Date.now(),
            seed: Math.floor(Math.random() * 1000000000),
            groupIds: [],
            frameId: null,
            boundElements: null,
            link: null,
            locked: false,
            index: null, // Fixed by syncInvalidIndices before updateScene below
            x: raw.x ?? 0,
            y: raw.y ?? 0,
            width: w,
            height: h,
            angle: raw.angle ?? 0,
            strokeColor: raw.strokeColor ?? "#1e1e1e",
            backgroundColor: raw.backgroundColor ?? "transparent",
            fillStyle: raw.fillStyle ?? "solid",
            strokeWidth: raw.strokeWidth ?? 2,
            strokeStyle: raw.strokeStyle ?? "solid",
            roughness: raw.roughness ?? 1,
            opacity: raw.opacity ?? 100,
            roundness: raw.roundness ?? null,
            ...(raw.type === "text"
              ? {
                  originalText: textOrOriginal,
                  text: raw.text ?? textOrOriginal,
                  textAlign: raw.textAlign ?? "left",
                }
              : {}),
            ...(pointsForLinear !== undefined
              ? { points: pointsForLinear }
              : {}),
          };
          newElements.push(newElement);
          hasChanges = true;
          addedCount++;
        } else if (op.type === "delete" && op.elementId) {
          const index = newElements.findIndex((el) => el.id === op.elementId);
          if (index !== -1) {
            const element = newElements[index];
            newElements[index] = {
              ...element,
              isDeleted: true,
              version: (element.version ?? 0) + 1,
              versionNonce: Math.floor(Math.random() * 1000000000),
              updated: Date.now(),
            };
            hasChanges = true;
            deletedCount++;
          }
        }
      }

      if (hasChanges) {
        // Sync fractional indices so new/updated elements pass validateFractionalIndices
        // (Scene.replaceAllElements validates before calling syncInvalidIndices)
        syncInvalidIndices(newElements);
        excalidrawAPI.updateScene({
          elements: newElements,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Generate change summary
        const summaryParts = [];
        if (addedCount > 0) {
          summaryParts.push(`Added ${addedCount} nodes`);
        }
        if (modifiedCount > 0) {
          summaryParts.push(`Modified ${modifiedCount} elements`);
        }
        if (deletedCount > 0) {
          summaryParts.push(`Deleted ${deletedCount} elements`);
        }
        setChangeSummary(`Change summary: ${summaryParts.join(", ")}`);

        // Scroll to added elements so user can see them
        if (addedCount > 0) {
          const addedElements = newElements.filter(
            (e) => !currentElements.find((ce) => ce.id === e.id),
          );
          if (addedElements.length > 0) {
            excalidrawAPI.scrollToContent(addedElements, {
              fitToContent: true,
            });
          }
        }
      }

      return hasChanges;
    },
    [excalidrawAPI],
  );

  // Format clarification response as user-friendly message
  const formatClarificationMessage = (
    clarification: ClarificationInfo,
  ): string => {
    const modeText =
      clarification.mode === "clarify"
        ? "I need some clarification before generating the diagram:"
        : "Please confirm my understanding:";

    const questionsText = clarification.questions
      .map((q) => `• ${q}`)
      .join("\n");

    return `${modeText}\n\n${questionsText}`;
  };

  // Handle text-to-diagram API for initial diagram creation (Mermaid-based, legacy)
  const handleTextToDiagramMermaid = async (
    text: string,
    signal?: AbortSignal,
  ): Promise<string> => {
    if (!excalidrawAPI) {
      throw new Error("Canvas not ready");
    }

    // Additional ready check - ensure canvas is fully initialized
    try {
      excalidrawAPI.getSceneElements();
    } catch (e) {
      console.warn("[AIWeaver] Canvas not fully ready, waiting...", e);
      // Wait a short time for canvas to initialize
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!excalidrawAPI) {
        throw new Error("Canvas initialization timeout");
      }
    }

    const backendUrl = `${getBackendUrl()}/v1/ai/text-to-diagram/chat-streaming`;

    // Use TTDStreamFetch to handle SSE streaming
    const result: TTDStreamFetchResult = await TTDStreamFetch({
      url: backendUrl,
      messages: [{ role: "user", content: text }],
      signal,
    });

    if (result.error) {
      throw result.error;
    }

    // Handle clarification response - return as normal message, not error
    if (result.clarification) {
      return formatClarificationMessage(result.clarification);
    }

    if (!result.generatedResponse) {
      throw new Error("No diagram generated");
    }

    // Parse Mermaid to Excalidraw elements
    const mermaidCode = result.generatedResponse;

    try {
      const parseResult = await parseMermaidToExcalidraw(mermaidCode);
      const { elements: skeletonElements, files } = parseResult;

      // FIX: Ensure all arrow/line elements have valid points before conversion
      // This fixes the "Cannot read properties of undefined (reading 'length')" error
      const fixedSkeletonElements = skeletonElements?.map((e: any) => {
        if (
          (e.type === "arrow" || e.type === "line") &&
          (!e.points || !Array.isArray(e.points) || e.points.length === 0)
        ) {
          const width = e.width || 100;
          const height = e.height || 0;
          return {
            ...e,
            points: [
              [0, 0],
              [width, height],
            ],
          };
        }
        return e;
      });

      // Convert to proper Excalidraw elements
      const elements = convertToExcalidrawElements(fixedSkeletonElements, {
        regenerateIds: true,
      });

      if (elements.length === 0) {
        throw new Error("Failed to parse the generated diagram");
      }

      // Add elements to canvas using addElementsFromPasteOrLibrary for proper positioning
      // This is the same method used by TTDDialog
      const app = excalidrawAPI as any;
      if (app.addElementsFromPasteOrLibrary) {
        app.addElementsFromPasteOrLibrary({
          elements,
          files: files || null,
          position: "center",
          fitToContent: true,
        });
      } else {
        // Fallback: directly update scene
        excalidrawAPI.updateScene({
          elements: [...excalidrawAPI.getSceneElements(), ...elements],
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Add files if any
        if (files) {
          excalidrawAPI.addFiles(Object.values(files));
        }

        // Scroll to the new content
        excalidrawAPI.scrollToContent(elements, { fitToContent: true });
      }

      setDiagramJustGenerated(true);
      return appT("aiWeaver.diagramCreated", { count: elements.length });
    } catch (parseError) {
      console.error("Mermaid parse error:", parseError);
      throw new Error(
        `Failed to convert the generated Mermaid diagram: ${
          parseError instanceof Error ? parseError.message : "Unknown error"
        }`,
      );
    }
  };

  // Handle generation API for initial diagram creation (New LangChain Pipeline)
  const handleTextToExcalidraw = async (
    text: string,
    signal?: AbortSignal,
  ): Promise<string | null> => {
    if (!excalidrawAPI) {
      throw new Error("Canvas not ready");
    }

    // Additional ready check - ensure canvas is fully initialized
    try {
      excalidrawAPI.getSceneElements();
    } catch (e) {
      console.warn("[AIWeaver] Canvas not fully ready, waiting...", e);
      // Wait a short time for canvas to initialize
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!excalidrawAPI) {
        throw new Error("Canvas initialization timeout");
      }
    }

    // Use new LangChain multi-agent pipeline
    const backendUrl = `${getBackendUrl()}/v1/ai/generation/streaming`;

    // When coming from Go to Draw with conversation context: send conversationContext so backend runs Summary then Generation. Use only once.
    const useConversationContext =
      initialConversationContext && !initialConversationContextUsedRef.current;
    if (useConversationContext) {
      initialConversationContextUsedRef.current = true;
      onConversationContextUsed?.();
    }
    const conversationContextToSend = useConversationContext
      ? initialConversationContext!
      : undefined;

    // When coming from Conversation with a pre-generated plan (legacy), send it. Use only once.
    const planToSend =
      !conversationContextToSend &&
      initialPlan &&
      text === initialPrompt &&
      !initialPlanUsedRef.current
        ? initialPlan
        : undefined;
    if (planToSend) {
      initialPlanUsedRef.current = true;
    }

    // Collect attachments: either from Draw Direct (one-shot from EntryScreen) or from sidebar input
    let imageToSend: { data: string; mimeType: string } | undefined;
    let filesToSend:
      | { contentBase64: string; mimeType: string; filename?: string }[]
      | undefined;

    // Source 1: Draw Direct attachments (one-shot from EntryScreen)
    if (!initialDrawAttachmentsUsedRef.current && (initialDrawImageUrls?.length || initialDrawFiles?.length)) {
      initialDrawAttachmentsUsedRef.current = true;
      if (initialDrawImageUrls?.length) {
        const first = initialDrawImageUrls[0];
        const match = first.match(/^data:([^;]+);base64,(.+)$/s);
        if (match) {
          imageToSend = { data: match[2], mimeType: match[1] };
        }
      }
      if (initialDrawFiles?.length) {
        filesToSend = initialDrawFiles.map((f) => ({
          contentBase64: f.contentBase64,
          mimeType: f.mimeType,
          filename: f.filename,
        }));
      }
      onDrawAttachmentsUsed?.();
    }

    // Source 2: Sidebar input attachments (current message)
    const sidebarAttach = sendAttachmentsRef.current;
    if (!imageToSend && sidebarAttach.imageUrls.length > 0) {
      const first = sidebarAttach.imageUrls[0];
      const match = first.match(/^data:([^;]+);base64,(.+)$/s);
      if (match) {
        imageToSend = { data: match[2], mimeType: match[1] };
      }
    }
    if (!filesToSend && sidebarAttach.files.length > 0) {
      filesToSend = sidebarAttach.files.map((f) => ({
        contentBase64: f.contentBase64,
        mimeType: f.mimeType,
        filename: f.filename,
      }));
    }
    sendAttachmentsRef.current = { imageUrls: [], files: [] };

    // Track first partial for scroll-on-first-batch; track if any partials ran for finalization
    let firstPartialDone = false;
    let hadPartialRenders = false;

    // Use TextToExcalidrawStreamFetch to handle SSE streaming with new pipeline
    const result = await TextToExcalidrawStreamFetch({
      url: backendUrl,
      messages: [{ role: "user", content: text }],
      chartType: selectedChartType,
      image: imageToSend,
      files: filesToSend,
      plan: planToSend,
      conversationContext: conversationContextToSend,
      userId: userId ?? undefined,
      anonymousId: userId ? undefined : anonymousId || undefined,
      useNewPipeline: true,
      signal,
      onPhaseUpdate: (phase, status) => {
        if (status === "started") {
          setWorkflowPhase(phase);
        } else if (status === "completed") {
          setWorkflowPhase(null);
        }
      },
      onPlanReceived: (plan) => {
        console.log("[Workflow] Search plan:", plan);
      },
      onRouteReceived: (route) => {
        console.log("[Workflow] Router decision:", route);
      },
      onSummaryReceived: (summary) => {
        summaryForDisplayAndRegenerateRef.current = summary;
        setMessages((prev) => {
          const next = [...prev];
          let lastUserIdx = -1;
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === "user") {
              lastUserIdx = i;
              break;
            }
          }
          if (lastUserIdx >= 0) {
            next[lastUserIdx] = { ...next[lastUserIdx], content: summary };
          }
          return next;
        });
      },
      onPartialElements: (partialElements, totalCount) => {
        const startTime = performance.now();

        // partialElements is the full accumulated skeleton list (all elements so far)
        console.log(
          `[AIWeaver] 🎨 Receiving partial: ${partialElements.length} elements so far (total: ${totalCount})`,
        );

        try {
          const convertStart = performance.now();
          const convertedElements = convertToExcalidrawElements(
            partialElements as any[],
            { regenerateIds: false },
          );
          const convertTime = performance.now() - convertStart;
          const reordered = reorderParentBeforeChild(convertedElements);

          excalidrawAPI!.updateScene({
            elements: reordered,
          });
          hadPartialRenders = true;
          const totalTime = performance.now() - startTime;

          console.log(
            `[AIWeaver] ✅ Rendered ${
              reordered.length
            } elements (total so far) | convert=${convertTime.toFixed(
              1,
            )}ms updateScene=${(totalTime - convertTime).toFixed(
              1,
            )}ms TOTAL=${totalTime.toFixed(1)}ms`,
          );

          if (!firstPartialDone && reordered.length > 0) {
            firstPartialDone = true;
            excalidrawAPI!.scrollToContent(reordered, {
              fitToContent: false,
            });
            // Switch to "Rendering..." once user sees content on canvas
            setWorkflowPhase("rendering");
          }
        } catch (partialRenderError) {
          console.error(
            "[AIWeaver] ⚠️ Partial render error:",
            partialRenderError,
          );
        }
      },
    });

    if (result.error) {
      throw result.error;
    }

    // Safety pre-check refusal: show message and do not generate
    if (result.safetyRefusal) {
      return result.safetyRefusal.message;
    }

    // Quota exceeded (24h window): show modal and do not enter pending state
    if (result.quotaExceeded) {
      // Refresh quota so the modal shows the latest data
      fetchQuota();
      setShowQuotaModal(true);
      return null; // signal: no assistant message to display
    }

    console.log("[AIWeaver] TextToExcalidrawStreamFetch result:", {
      hasElements: !!result.elements,
      elementsCount: result.elements?.length || 0,
      hasFiles: !!result.files,
      filesCount: result.files ? Object.keys(result.files).length : 0,
      hasError: !!result.error,
      hasRawCode: !!result.rawCode,
    });

    // Additional validation logging
    if (result.elements) {
      console.log(
        "[AIWeaver] 📋 Elements sample (first 2):",
        result.elements.slice(0, 2),
      );
    }

    if (result.files) {
      console.log("[AIWeaver] 📁 Files received:");
      for (const [fileId, fileData] of Object.entries(result.files)) {
        console.log(`  - ${fileId}:`, {
          mimeType: fileData.mimeType,
          hasDataURL: !!fileData.dataURL,
          dataURLLength: fileData.dataURL?.length || 0,
          created: fileData.created,
        });
      }
    }

    // When backend sends 0 elements in optimized (e.g. validation failed) but we already
    // rendered via partial streaming, use canvas elements so we do not throw.
    let effectiveElements: any[] = result.elements ?? [];
    if (
      effectiveElements.length === 0 &&
      hadPartialRenders &&
      excalidrawAPI.getSceneElements().length > 0
    ) {
      console.log(
        "[AIWeaver] ⚠️ Backend sent 0 elements in optimized but we have partial renders on canvas; using canvas as source of truth",
      );
      effectiveElements = excalidrawAPI.getSceneElements() as any[];
    }
    if (effectiveElements.length === 0) {
      console.error("[AIWeaver] ❌ No elements received from backend");
      console.error("[AIWeaver] Full result:", result);
      throw new Error("No diagram generated");
    }

    try {
      // If we already rendered elements progressively, handle final updates
      if (hadPartialRenders) {
        console.log(
          "[AIWeaver] 🔄 Finalizing progressively rendered elements...",
        );

        // Convert all final elements
        const finalElements = convertToExcalidrawElements(
          effectiveElements as any[],
          { regenerateIds: false },
        );

        // Get current canvas elements
        const currentElements = excalidrawAPI.getSceneElements();
        const elementsMap = new Map(
          currentElements.map((el: any) => [el.id, el]),
        );

        // Update existing elements and add any new ones (edge case)
        const updatedElements = finalElements.map((finalEl) => {
          if (elementsMap.has(finalEl.id)) {
            const existing = elementsMap.get(finalEl.id);
            return { ...existing, ...finalEl };
          }
          return finalEl;
        });
        const reordered = reorderParentBeforeChild(updatedElements);

        // Prepare files for Excalidraw (icons from backend)
        let files: Record<string, any> | null = null;
        if (result.files && Object.keys(result.files).length > 0) {
          console.log("[AIWeaver] 📁 Adding icon files...");
          files = {};
          for (const [fileId, fileData] of Object.entries(result.files)) {
            files[fileId] = {
              id: fileId,
              dataURL: fileData.dataURL,
              mimeType: fileData.mimeType,
              created: fileData.created || Date.now(),
              lastRetrieved: Date.now(),
            };
          }
          console.log(
            "[AIWeaver] ✅ Files prepared:",
            Object.keys(files).length,
          );
        }

        // Order: frontend reorder parent-before-child
        excalidrawAPI.updateScene({
          elements: reordered,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Add files separately
        if (files) {
          excalidrawAPI.addFiles(Object.values(files));
        }

        // Fit content to view
        excalidrawAPI.scrollToContent(reordered, { fitToContent: true });

        console.log("[AIWeaver] ✅ Progressive rendering finalized");

        // Send telemetry report (always send, even if rendering failed, for E2E diagnostics)
        if (result.correlationId) {
          console.log("[AIWeaver] 📊 Sending telemetry report...");
          try {
            await collectAndSendReport(
              excalidrawAPI,
              result.correlationId,
              text,
              selectedChartType,
              false, // enableFeedbackLoop
              { codeReceived: result.rawCode },
            );
            console.log("[AIWeaver] ✅ Telemetry sent successfully");
          } catch (telemetryError) {
            console.error("[AIWeaver] ❌ Telemetry failed:", telemetryError);
            // Don't fail the main flow if telemetry fails
          }
        } else {
          console.warn("[AIWeaver] ⚠️ No correlationId, skipping telemetry");
        }

        setDiagramJustGenerated(true);
        return appT("aiWeaver.diagramCreatedStreamed", { count: reordered.length });
      }

      // Fallback: No progressive rendering occurred, use standard flow
      console.log("[AIWeaver] 🔄 Converting elements (standard flow)...", {
        inputCount: result.elements.length,
        sampleElement: result.elements[0],
      });

      const elements = convertToExcalidrawElements(result.elements as any[], {
        regenerateIds: true,
      });
      const reordered = reorderParentBeforeChild(elements);

      console.log("[AIWeaver] ✅ Converted elements:", {
        outputCount: reordered.length,
        sampleElement: reordered[0],
      });

      if (reordered.length === 0) {
        console.error("[AIWeaver] ❌ Conversion produced 0 elements");
        console.error("[AIWeaver] Input elements:", result.elements);
        throw new Error("Failed to convert the generated diagram");
      }

      // Prepare files for Excalidraw (convert from backend format)
      let files: Record<string, any> | null = null;
      if (result.files && Object.keys(result.files).length > 0) {
        console.log("[AIWeaver] 📁 Preparing files for canvas...");
        files = {};
        for (const [fileId, fileData] of Object.entries(result.files)) {
          // Ensure all required fields are present
          const preparedFile = {
            id: fileId,
            dataURL: fileData.dataURL,
            mimeType: fileData.mimeType,
            created: fileData.created || Date.now(),
            lastRetrieved: Date.now(), // Add lastRetrieved timestamp
          };

          console.log(`[AIWeaver] Prepared file ${fileId}:`, {
            mimeType: preparedFile.mimeType,
            hasDataURL: !!preparedFile.dataURL,
            dataURLPrefix: preparedFile.dataURL?.substring(0, 30),
            created: preparedFile.created,
            lastRetrieved: preparedFile.lastRetrieved,
          });

          files[fileId] = preparedFile;
        }
        console.log("[AIWeaver] ✅ Files prepared:", Object.keys(files).length);
      } else {
        console.log("[AIWeaver] ℹ️ No files to add");
      }

      // Add elements to canvas
      console.log("[AIWeaver] 🎨 Adding elements to canvas...", {
        elementsCount: reordered.length,
        filesCount: files ? Object.keys(files).length : 0,
        canvasAPI: !!excalidrawAPI,
        hasAddElementsFromPasteOrLibrary: !!(excalidrawAPI as any)
          .addElementsFromPasteOrLibrary,
      });

      // Log sample of elements being added
      console.log("[AIWeaver] Sample elements to add:", reordered.slice(0, 2));

      const app = excalidrawAPI as any;
      if (app.addElementsFromPasteOrLibrary) {
        console.log("[AIWeaver] ✅ Using addElementsFromPasteOrLibrary");
        app.addElementsFromPasteOrLibrary({
          elements: reordered,
          files,
          position: "center",
          fitToContent: true,
        });
        console.log(
          "[AIWeaver] ✅ Elements added successfully via addElementsFromPasteOrLibrary",
        );
      } else {
        console.log("[AIWeaver] ⚠️ Using updateScene (fallback)");
        const combined = [...excalidrawAPI.getSceneElements(), ...reordered];
        const combinedReordered = reorderParentBeforeChild(combined);
        excalidrawAPI.updateScene({
          elements: combinedReordered,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Add files if any
        if (files) {
          console.log("[AIWeaver] Adding files via addFiles...");
          excalidrawAPI.addFiles(Object.values(files));
          console.log("[AIWeaver] ✅ Files added");
        }

        // Scroll to the new content
        console.log("[AIWeaver] Scrolling to content...");
        excalidrawAPI.scrollToContent(combinedReordered, {
          fitToContent: true,
        });
        console.log("[AIWeaver] ✅ Scrolled to content");
      }

      console.log("[AIWeaver] 🎉 Successfully added elements to canvas");

      // Verify elements are on canvas
      const currentElements = excalidrawAPI.getSceneElements();
      console.log("[AIWeaver] 📊 Canvas state after adding:", {
        totalElements: currentElements.length,
        visibleElements: currentElements.filter((e: any) => !e.isDeleted)
          .length,
      });

      // Send telemetry report
      if (result.correlationId) {
        console.log("[AIWeaver] 📊 Sending telemetry report...");
        try {
          await collectAndSendReport(
            excalidrawAPI,
            result.correlationId,
            text,
            selectedChartType,
            false, // enableFeedbackLoop
            { codeReceived: result.rawCode },
          );
        } catch (telemetryError) {
          console.error("[AIWeaver] Telemetry failed:", telemetryError);
          // Don't fail the main flow if telemetry fails
        }
      }

      setDiagramJustGenerated(true);
      return appT("aiWeaver.diagramCreated", { count: elements.length });
    } catch (convertError) {
      console.error(
        "[AIWeaver] ❌ Element conversion/addition error:",
        convertError,
      );
      console.error("[AIWeaver] Stack trace:", (convertError as Error).stack);

      // Collect error for telemetry
      errorCollector.pushError(
        `Element conversion failed: ${
          convertError instanceof Error
            ? convertError.message
            : String(convertError)
        }`,
        { phase: "element_conversion" },
      );

      throw new Error(
        `Failed to convert the generated diagram: ${
          convertError instanceof Error ? convertError.message : "Unknown error"
        }`,
      );
    }
  };

  // Handle text-to-diagram - choose between direct Excalidraw or Mermaid based on settings
  const handleTextToDiagram = async (
    text: string,
    signal?: AbortSignal,
  ): Promise<string | null> => {
    if (useDirectExcalidraw) {
      return handleTextToExcalidraw(text, signal);
    }
    return handleTextToDiagramMermaid(text, signal);
  };

  // Handle canvas edit API for modifying existing elements
  const handleCanvasEdit = async (
    text: string,
    signal?: AbortSignal,
  ): Promise<string | null> => {
    if (!excalidrawAPI) {
      throw new Error("Canvas not ready");
    }

    // Additional ready check - ensure canvas is fully initialized
    try {
      excalidrawAPI.getSceneElements();
    } catch (e) {
      console.warn("[AIWeaver] Canvas not fully ready, waiting...", e);
      // Wait a short time for canvas to initialize
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!excalidrawAPI) {
        throw new Error("Canvas initialization timeout");
      }
    }

    const elements = excalidrawAPI.getSceneElements();
    const appState = excalidrawAPI.getAppState();
    const selectedIds = Object.keys(appState.selectedElementIds);

    // Prepare elements data
    const elementsData = elements
      .filter((el) => !el.isDeleted)
      .map((el) => {
        const base = {
          id: el.id,
          type: el.type,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          strokeColor: el.strokeColor,
          backgroundColor: el.backgroundColor,
        };
        if ("text" in el) {
          return { ...base, text: el.text };
        }
        return base;
      });

    const backendUrl = `${getBackendUrl()}/v1/ai/canvas/edit`;

    let response: Response;
    try {
      response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: text,
          elements: elementsData,
          selectedIds,
        }),
        signal,
      });
    } catch (fetchError) {
      const errorMsg =
        fetchError instanceof Error ? fetchError.message : "Network error";
      throw new Error(
        `Failed to connect to backend (${backendUrl}): ${errorMsg}. ` +
          `Please ensure the backend server is running and VITE_APP_AI_BACKEND is configured correctly.`,
      );
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.detail || errorJson.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    const data: CanvasEditResponse = await response.json();

    if (data.action === "regenerate" && data.regenerate_prompt) {
      excalidrawAPI.updateScene({ elements: [] });
      return await handleTextToDiagram(data.regenerate_prompt, signal);
    }

    if (data.error) {
      return `Sorry, I encountered an issue: ${data.error}`;
    } else if (data.operations && data.operations.length > 0) {
      executeOperations(data.operations);
      return `I've updated the canvas based on your request. ${data.operations.length} change(s) were made.`;
    }
    return "I understand your request, but I couldn't determine specific changes to make. Could you please be more specific?";
  };

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    const hasAttachments =
      pendingImageUrls.length > 0 || pendingFiles.length > 0;
    if ((!text && !hasAttachments) || isLoading) {
      return;
    }

    // Pre-check: if quota is known to be exhausted, show the limit modal immediately
    // When limit=0 (unlimited mode), never block
    if (quota && quota.limit > 0 && quota.remaining <= 0) {
      setShowQuotaModal(true);
      return;
    }

    // Capture attachments into ref before clearing state (generation helpers read from ref)
    sendAttachmentsRef.current = {
      imageUrls: [...pendingImageUrls],
      files: [...pendingFiles],
    };

    // Resolve attachment sources: sidebar input OR EntryScreen initial props (one-shot)
    const msgImageUrls =
      pendingImageUrls.length > 0
        ? [...pendingImageUrls]
        : !initialDrawAttachmentsUsedRef.current && initialDrawImageUrls?.length
          ? [...initialDrawImageUrls]
          : undefined;
    const msgFileMeta =
      pendingFiles.length > 0
        ? pendingFiles.map((f) => ({ filename: f.filename, mimeType: f.mimeType }))
        : !initialDrawAttachmentsUsedRef.current && initialDrawFiles?.length
          ? initialDrawFiles.map((f) => ({ filename: f.filename, mimeType: f.mimeType }))
          : undefined;

    const userMessage: Message = {
      id: randomId(),
      role: "user",
      content: text || " ",
      timestamp: new Date(),
      imageUrls: msgImageUrls,
      attachmentFiles: msgFileMeta,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setPendingImageUrls([]);
    setPendingFiles([]);
    setIsLoading(true);
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      if (!excalidrawAPI) {
        throw new Error("Canvas not ready");
      }

      // Auto-create a new diagram if user is authenticated but no project is selected
      // This happens when user starts typing on a fresh canvas after login
      // When NOT authenticated, skip this - user can still use the app, data just won't persist to cloud
      if (isAuthenticated && !currentDiagramId && onCreateNewDiagram) {
        if (import.meta.env.DEV) {
          console.log(
            "[AIWeaverSidebar] No project selected, auto-creating new diagram",
          );
        }
        // Set flag to prevent message reset when diagram ID changes
        isAutoCreatingDiagramRef.current = true;
        const newDiagram = await onCreateNewDiagram(
          undefined,
          undefined,
          getPendingChatHistoryForDiagram() ?? undefined,
        );
        if (!newDiagram) {
          isAutoCreatingDiagramRef.current = false;
          throw new Error("Failed to create new diagram");
        }
        if (import.meta.env.DEV) {
          console.log("[AIWeaverSidebar] Auto-created diagram:", newDiagram.id);
        }
        // Capture the id immediately — props may not update synchronously
        lastKnownDiagramIdRef.current = newDiagram.id;
      } else if (!isAuthenticated && import.meta.env.DEV) {
        console.log(
          "[AIWeaverSidebar] Not authenticated - proceeding without cloud sync",
        );
      }

      // Regenerate intent: user asked to regenerate (e.g. "regenerate", "重新生成")
      // Treat as new generation with the same prompt; clear canvas so new result replaces previous
      let effectivePrompt = text;
      const currentElementsBefore = excalidrawAPI
        .getSceneElements()
        .filter((el) => !el.isDeleted);
      const hadElements = currentElementsBefore.length > 0;
      if (hadElements && isRegenerateIntent(text) && lastAction != null) {
        // Restore canvas to pre-action snapshot, then re-use the last prompt
        const snapshot = snapshotBeforeLastActionRef.current;
        excalidrawAPI.updateScene({
          elements: snapshot ? [...snapshot] : [],
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
        effectivePrompt = lastAction.prompt;
        if (import.meta.env.DEV) {
          console.log(
            "[AIWeaverSidebar] Regenerate intent: clearing canvas and using last prompt",
          );
        }
      }

      // Determine which API to use based on canvas state
      // If canvas is empty (no elements), use text-to-diagram to create initial diagram
      // If canvas has elements, use canvas-edit to modify existing elements
      const currentElements = excalidrawAPI
        .getSceneElements()
        .filter((el) => !el.isDeleted);
      const isEmptyCanvas = currentElements.length === 0;

      // Check if this is the first user message (for auto-renaming)
      // Count only user messages (excluding system messages if any)
      // IMPORTANT: Check BEFORE adding the new message to the state
      const userMessagesBeforeThis = messages.filter(
        (m) => m.role === "user",
      ).length;
      const isFirstMessage = userMessagesBeforeThis === 0;

      if (import.meta.env.DEV) {
        console.log("[AIWeaverSidebar] Message check:", {
          totalMessages: messages.length,
          userMessagesBefore: userMessagesBeforeThis,
          isFirstMessage,
          currentDiagramName,
        });
      }

      // Auto-rename diagram IMMEDIATELY if this is the first message
      // Run in parallel with diagram generation for better UX
      if (isFirstMessage) {
        maybeAutoRenameDiagram(text).catch((e) => {
          console.warn("[Naming] auto-rename failed (non-blocking)", e);
        });
      }

      let assistantContent: string | null;

      // Snapshot canvas BEFORE the action so Regenerate can restore it
      const elementsBeforeAction = excalidrawAPI
        .getSceneElements()
        .filter((el: any) => !el.isDeleted);

      if (isEmptyCanvas) {
        // Use text-to-diagram API for initial creation (or regenerate with same prompt)
        assistantContent = await handleTextToDiagram(effectivePrompt, signal);
        if (assistantContent !== null) {
          snapshotBeforeLastActionRef.current = elementsBeforeAction;
          const promptForRegenerate =
            summaryForDisplayAndRegenerateRef.current ?? effectivePrompt;
          if (summaryForDisplayAndRegenerateRef.current) {
            summaryForDisplayAndRegenerateRef.current = null;
          }
          setLastAction({ prompt: promptForRegenerate, type: "generation" });
        }
      } else {
        // Use canvas-edit API for modification
        assistantContent = await handleCanvasEdit(text, signal);
        if (assistantContent !== null) {
          snapshotBeforeLastActionRef.current = elementsBeforeAction;
          setLastAction({ prompt: text, type: "edit" });
        }
      }

      // When assistantContent is null, quota modal was triggered — skip chat message
      if (assistantContent !== null) {
        const assistantMessage: Message = {
          id: randomId(),
          role: "assistant",
          content: assistantContent,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      const isAborted = error instanceof Error && error.name === "AbortError";
      if (isAborted) {
        // User stopped generation: no error message, attempt is finished
        if (import.meta.env.DEV) {
          console.log("[AIWeaverSidebar] Generation stopped by user");
        }
      } else {
        console.error("AI Weaver error:", error);
        const errorMessage: Message = {
          id: randomId(),
          role: "assistant",
          content: `I'm sorry, I encountered an error: ${
            error instanceof Error ? error.message : "Unknown error"
          }. Please try again.`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } finally {
      setIsLoading(false);
      setWorkflowPhase(null);
      abortControllerRef.current = null;
    }
  };

  const handleStopGeneration = () => {
    abortControllerRef.current?.abort();
  };

  /** Regenerate: restore canvas to pre-action snapshot, then re-send the same prompt. */
  const handleRegenerate = () => {
    if (!lastAction || isLoading || !excalidrawAPI) {
      return;
    }

    // Restore canvas to the snapshot taken before the last action
    const snapshot = snapshotBeforeLastActionRef.current;
    excalidrawAPI.updateScene({
      elements: snapshot ? [...snapshot] : [],
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });

    handleSendMessage(lastAction.prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (e.nativeEvent.isComposing || skipNextEnterRef.current) {
        skipNextEnterRef.current = false;
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      handleSendMessage();
    }
    e.stopPropagation();
  };

  const handleCompositionEnd = () => {
    skipNextEnterRef.current = true;
    setTimeout(() => {
      skipNextEnterRef.current = false;
    }, 100);
  };

  const handleVoiceTranscript = useCallback((text: string) => {
    setInputValue((prev) => (prev ? `${prev} ${text}` : text));
    textareaRef.current?.focus();
  }, []);

  // ── Drag-and-drop for images and files ──
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    const fileList = e.dataTransfer.files;
    if (!fileList?.length) {
      return;
    }

    const imageFilesDT = new DataTransfer();
    const docFilesDT = new DataTransfer();
    for (const f of Array.from(fileList)) {
      if (f.type.startsWith("image/")) {
        imageFilesDT.items.add(f);
      } else {
        docFilesDT.items.add(f);
      }
    }

    const allRejected: string[] = [];
    const collectRejected = (names: string[]) => allRejected.push(...names);

    if (imageFilesDT.files.length > 0) {
      const urls = await readFilesAsDataUrls(imageFilesDT.files, collectRejected);
      if (urls.length) {
        setPendingImageUrls((prev) => [...prev, ...urls]);
      }
    }
    if (docFilesDT.files.length > 0) {
      const files = await readFilesAsBase64(docFilesDT.files, collectRejected);
      if (files.length) {
        setPendingFiles((prev) => [...prev, ...files]);
      }
    }

    if (allRejected.length) {
      showFileTooLarge(allRejected);
    }

    textareaRef.current?.focus();
  }, [showFileTooLarge]);

  if (isCollapsed) {
    return (
      <div className="ai-weaver-sidebar ai-weaver-sidebar--collapsed">
        <button
          className="ai-weaver-sidebar__expand-btn"
          onClick={onToggleCollapse}
          title={appT("aiWeaver.openTitle")}
        >
          <span className="material-symbols-outlined">lightbulb</span>
        </button>
      </div>
    );
  }

  return (
    <>
      <aside className="ai-weaver-sidebar">
        <div className="ai-weaver-sidebar__container">
          {/* Header */}
          <div className="ai-weaver-sidebar__header">
            <div className="ai-weaver-sidebar__header-left"></div>
            <div className="ai-weaver-sidebar__title">
              <span className="material-symbols-outlined ai-weaver-sidebar__icon">
                lightbulb
              </span>
              <h2>{appT("aiWeaver.title")}</h2>
            </div>
            <button
              className="ai-weaver-sidebar__collapse-btn"
              onClick={onToggleCollapse}
              title={appT("aiWeaver.collapse")}
            >
              <span className="material-symbols-outlined">
                keyboard_double_arrow_left
              </span>
            </button>
          </div>

          {/* Messages */}
          <div className="ai-weaver-sidebar__messages">
            {messages.length === 0 && (
              <div className="ai-weaver-sidebar__empty">
                <span className="material-symbols-outlined">chat_bubble</span>
                <p>{appT("aiWeaver.emptyHint")}</p>
              </div>
            )}
            {messages.map((message, idx) => {
              // Show regenerate icon on the last assistant message when idle
              const isLastAssistant =
                message.role === "assistant" &&
                !isLoading &&
                !!lastAction &&
                idx ===
                  messages.reduce(
                    (last, m, i) => (m.role === "assistant" ? i : last),
                    -1,
                  );

              return (
                <div
                  key={message.id}
                  className={`ai-weaver-sidebar__message ai-weaver-sidebar__message--${message.role}`}
                >
                  <div className="ai-weaver-sidebar__message-content">
                    {message.role === "user" && (
                      <MessageAttachments
                        imageUrls={message.imageUrls}
                        files={message.attachmentFiles}
                      />
                    )}
                    {message.content}
                  </div>
                  {isLastAssistant && (
                    <button
                      type="button"
                      className="ai-weaver-sidebar__regen-icon"
                      onClick={handleRegenerate}
                      title={appT("aiWeaver.regenerateTitle")}
                    >
                      <span className="material-symbols-outlined">replay</span>
                    </button>
                  )}
                </div>
              );
            })}
            {isLoading && (
              <div className="ai-weaver-sidebar__message ai-weaver-sidebar__message--assistant">
                <div className="ai-weaver-sidebar__message-content ai-weaver-sidebar__message-content--loading">
                  <div className="ai-weaver-sidebar__workflow-phase">
                    <span className="ai-weaver-sidebar__phase-icon">⚙️</span>
                    <span className="ai-weaver-sidebar__phase-text">
                      {workflowPhase === "rendering"
                        ? "Rendering..."
                        : "Connecting..."}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="ai-weaver-sidebar__input-area">
            {changeSummary && (
              <div className="ai-weaver-sidebar__change-summary">
                <span className="material-symbols-outlined">check_circle</span>
                <span>{changeSummary}</span>
              </div>
            )}
            <div
              className={`ai-weaver-sidebar__input-wrapper${
                isDragOver ? " ai-weaver-sidebar__input-wrapper--dragover" : ""
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <AttachImageThumbnails
                urls={pendingImageUrls}
                onRemove={(i) =>
                  setPendingImageUrls((prev) =>
                    prev.filter((_, j) => j !== i),
                  )
                }
                className="ai-weaver-sidebar__thumbnails"
              />
              <AttachFileThumbnails
                files={pendingFiles}
                onRemove={(i) =>
                  setPendingFiles((prev) =>
                    prev.filter((_, j) => j !== i),
                  )
                }
                className="ai-weaver-sidebar__file-list"
              />
              <textarea
                ref={textareaRef}
                className="ai-weaver-sidebar__textarea"
                placeholder={appT("aiWeaver.placeholder")}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onCompositionEnd={handleCompositionEnd}
                disabled={isLoading}
              />
              <div className="ai-weaver-sidebar__input-actions">
                <VoiceInputButton
                  onTranscript={handleVoiceTranscript}
                  disabled={isLoading}
                  variant="default"
                  layout="default"
                  className="ai-weaver-sidebar__voice-btn"
                />
                <div className="ai-weaver-sidebar__input-actions-right">
                  <AttachButton
                    onAddImages={(urls) => {
                      setPendingImageUrls((prev) => [...prev, ...urls]);
                      textareaRef.current?.focus();
                    }}
                    onAddFiles={(files) => {
                      setPendingFiles((prev) => [...prev, ...files]);
                      textareaRef.current?.focus();
                    }}
                    onFileTooLarge={showFileTooLarge}
                    disabled={isLoading}
                    buttonClassName="ai-weaver-sidebar__attach-btn"
                    title={appT("conversation.addAttachment") ?? "Add image / file"}
                  />
                  {isLoading ? (
                    <button
                      type="button"
                      className="ai-weaver-sidebar__stop-btn"
                      onClick={handleStopGeneration}
                      title={appT("aiWeaver.stop")}
                    >
                      <span className="material-symbols-outlined">
                        stop_circle
                      </span>
                    </button>
                  ) : (
                    <button
                      className="ai-weaver-sidebar__send-btn"
                      onClick={() => handleSendMessage()}
                      disabled={
                        !inputValue.trim() &&
                        pendingImageUrls.length === 0 &&
                        pendingFiles.length === 0
                      }
                      title={appT("aiWeaver.send")}
                    >
                      <span className="material-symbols-outlined">
                        arrow_forward
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Quota limit modal (Portal → body, escapes sidebar stacking context) ── */}
      {showQuotaModal &&
        createPortal(
          <div className="iw-quota-modal" role="presentation">
            <div
              className="iw-quota-modal__backdrop"
              onClick={() => setShowQuotaModal(false)}
            />
            <div
              className="iw-quota-modal__panel"
              role="dialog"
              aria-modal="true"
              aria-label="Quota limit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative tape strip */}
              <div className="iw-quota-modal__tape" aria-hidden="true" />

              <button
                type="button"
                className="iw-quota-modal__close"
                onClick={() => setShowQuotaModal(false)}
                aria-label="Close"
              >
                <span className="material-symbols-outlined">close</span>
              </button>

              <div className="iw-quota-modal__body">
                <span
                  className="material-symbols-outlined iw-quota-modal__icon"
                  aria-hidden="true"
                >
                  hourglass_top
                </span>

                <h2 className="iw-quota-modal__title">
                  {appT("aiWeaver.quotaModalTitle")}
                </h2>

                <p className="iw-quota-modal__desc">
                  {quota?.isAnonymous
                    ? appT("aiWeaver.quotaModalAnonDesc")
                    : appT("aiWeaver.quotaModalUserDesc")}
                </p>

                {quota?.isAnonymous ? (
                  <button
                    type="button"
                    className="iw-quota-modal__cta"
                    onClick={() => {
                      setShowQuotaModal(false);
                      // Flag: anonymous user on canvas wants to return after registration
                      try {
                        sessionStorage.setItem(
                          "ideaweave:returnToCanvasAfterAuth",
                          "true",
                        );
                        // Backup canvas state to sessionStorage as a safety net
                        // (in case localStorage gets cleared during OAuth redirect)
                        const els = localStorage.getItem("excalidraw");
                        const state = localStorage.getItem("excalidraw-state");
                        if (els) {
                          sessionStorage.setItem(
                            "ideaweave:canvasBackup:elements",
                            els,
                          );
                        }
                        if (state) {
                          sessionStorage.setItem(
                            "ideaweave:canvasBackup:appState",
                            state,
                          );
                        }
                        if (import.meta.env.DEV) {
                          console.log(
                            "[IdeaWeave] Saved canvas backup for post-auth restore",
                          );
                        }
                      } catch (e) {
                        console.error(
                          "[IdeaWeave] Failed to save canvas backup:",
                          e,
                        );
                      }
                      onOpenAuthModal?.();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      person_add
                    </span>
                    {appT("aiWeaver.quotaModalSignUp")}
                  </button>
                ) : (
                  <div className="iw-quota-modal__upgrade-group">
                    <button
                      type="button"
                      className="iw-quota-modal__cta"
                      disabled={isUpgradeLoading}
                      onClick={handleUpgradeToPro}
                    >
                      {isUpgradeLoading ? (
                        <span className="material-symbols-outlined iw-quota-modal__spin">
                          progress_activity
                        </span>
                      ) : (
                        <span className="material-symbols-outlined">
                          workspace_premium
                        </span>
                      )}
                      {isUpgradeLoading ? "Redirecting…" : "Upgrade to Pro"}
                    </button>
                    <button
                      type="button"
                      className="iw-quota-modal__ok"
                      onClick={() => setShowQuotaModal(false)}
                    >
                      {appT("aiWeaver.quotaModalOk")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
      {toastMessage && (
        <UploadToast message={toastMessage} onClose={dismissToast} />
      )}
    </>
  );
};
